import { create } from 'zustand';
import { Flow as SharedFlow, NodeType } from '@dynamic-flow/types';
import { appConfig } from '../config/appConfig';
import { createThemePreset } from '@dynamic-flow/ui';

interface FlowNode {
  id: string;
  type: NodeType;
  position: { x: number; y: number };
  data: {
    label: string;
    required?: boolean;
    description?: string;
  };
  connections: any[];
}

interface FlowStore {
  flows: SharedFlow[];
  currentFlow: SharedFlow | null;
  isLoading: boolean;
  error: string | null;

  // Actions
  setCurrentFlow: (flow: SharedFlow) => void;
  addNode: (node: FlowNode) => void;
  updateNode: (nodeId: string, updates: Partial<FlowNode>) => void;
  deleteNode: (nodeId: string) => void;
  updateFlowSettings: (settings: Partial<SharedFlow>) => void;
  saveFlow: (flow: SharedFlow) => Promise<void>;
  publishFlow: (flowId: string) => Promise<void>;
  unpublishFlow: (flowId: string) => Promise<void>;
  deleteFlow: (flowId: string) => Promise<void>;
  loadFlows: () => Promise<void>;
  createNewFlow: (title?: string) => Promise<SharedFlow>;
  saveFlowTranslation: (flow: SharedFlow, lang: string) => Promise<void>;
}

// Helper function to convert string dates to Date objects
function convertFlowDates(flow: any): SharedFlow {
  return {
    ...flow,
    createdAt: new Date(flow.createdAt),
    updatedAt: new Date(flow.updatedAt),
    publishedAt: flow.publishedAt ? new Date(flow.publishedAt) : undefined,
    startDate: flow.startDate ? new Date(flow.startDate) : undefined,
    endDate: flow.endDate ? new Date(flow.endDate) : undefined,
  };
}

// Create default advanced theme
const defaultAdvancedTheme = createThemePreset(
  'default-advanced',
  'Default Advanced Theme',
  '#3b82f6', // Primary color
  '#64748b', // Secondary color
  'Inter, system-ui, sans-serif'
);

const defaultTheme = {
  id: appConfig.ui.defaultTheme,
  name: `${appConfig.ui.defaultTheme.charAt(0).toUpperCase() + appConfig.ui.defaultTheme.slice(1)} Theme`,
  description: 'Default theme',
  colors: {
    primary: {
      main: '#3b82f6',
      light: '#60a5fa',
      dark: '#1d4ed8',
      contrastText: '#ffffff',
    },
    secondary: {
      main: '#64748b',
      light: '#94a3b8',
      dark: '#475569',
      contrastText: '#ffffff',
    },
    background: {
      default: '#ffffff',
      paper: '#ffffff',
      accent: '#f8fafc',
    },
    text: {
      primary: '#0f172a',
      secondary: '#64748b',
      disabled: '#cbd5e1',
    },
    border: {
      light: '#f1f5f9',
      main: '#e2e8f0',
      dark: '#cbd5e1',
    },
  },
  typography: {
    fontFamily: 'Inter, system-ui, sans-serif',
    fontSizes: {
      xs: '0.75rem',
      sm: '0.875rem',
      base: '1rem',
      lg: '1.125rem',
      xl: '1.25rem',
      '2xl': '1.5rem',
      '3xl': '1.875rem',
    },
    fontWeights: {
      normal: 400,
      medium: 500,
      semibold: 600,
      bold: 700,
    },
  },
  spacing: {
    xs: '0.25rem',
    sm: '0.5rem',
    md: '1rem',
    lg: '1.5rem',
    xl: '2rem',
    '2xl': '3rem',
  },
  borderRadius: {
    sm: '0.25rem',
    md: '0.375rem',
    lg: '0.5rem',
    xl: '0.75rem',
  },
  shadows: {
    sm: '0 1px 2px 0 rgb(0 0 0 / 0.05)',
    md: '0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1)',
    lg: '0 10px 15px -3px rgb(0 0 0 / 0.1), 0 4px 6px -4px rgb(0 0 0 / 0.1)',
  },
  advanced: defaultAdvancedTheme,
};

export const useFlowStore = create<FlowStore>((set, get) => ({
  flows: [],
  currentFlow: {
    id: 'registration_mhai',
    title: 'Registration Mhai',
    nodes: [
      {
        id: 'contact_information',
        type: 'text_input' as NodeType,
        position: { x: 100, y: 100 },
        data: {
          label: 'Contact Information',
          description: 'Please provide your contact details',
          required: true,
        },
        connections: [{ targetNodeId: 'how_many_people' }],
      },
      {
        id: 'how_many_people',
        type: 'number_input' as NodeType,
        position: { x: 100, y: 300 },
        data: {
          label: 'How Many People',
          description: 'How many guests will you bring?',
          required: true,
        },
        connections: [
          {
            targetNodeId: 'guests',
            condition: {
              field: 'number_of_guests',
              operator: 'greater_than',
              value: 0,
            },
          },
        ],
      },
      {
        id: 'guests',
        type: 'single_choice' as NodeType,
        position: { x: 100, y: 500 },
        data: {
          label: 'Guest Information',
          description: 'Please provide details for each guest',
          required: true,
        },
        connections: [{ targetNodeId: 'thank_you' }],
      },
      {
        id: 'thank_you',
        type: 'thank_you',
        position: { x: 100, y: 700 },
        data: {
          label: 'Thank You!',
          description:
            'Your registration has been submitted successfully. We will contact you soon with more details.',
          required: false,
        },
        connections: [],
      },
    ],
    settings: {
      allowMultipleSubmissions: false,
      showProgressBar: true,
      requireAuth: false,
      collectAnalytics: appConfig.features.analytics,
    },
    theme: { ...defaultTheme },
    createdAt: new Date(),
    updatedAt: new Date(),
    status: 'draft',
  },
  isLoading: false,
  error: null,

  setCurrentFlow: (flow) => set({ currentFlow: flow }),

  addNode: (node) => {
    const { currentFlow } = get();
    if (!currentFlow) return;

    const updatedFlow = {
      ...currentFlow,
      nodes: [...currentFlow.nodes, node],
      updatedAt: new Date(),
    };

    set({ currentFlow: updatedFlow });
  },

  updateNode: (nodeId, updates) => {
    const { currentFlow } = get();
    if (!currentFlow) return;

    const updatedFlow = {
      ...currentFlow,
      nodes: currentFlow.nodes.map((node) =>
        node.id === nodeId ? { ...node, ...updates } : node
      ),
      updatedAt: new Date(),
    };

    set({ currentFlow: updatedFlow });
  },

  deleteNode: (nodeId) => {
    const { currentFlow } = get();
    if (!currentFlow) return;

    const updatedFlow = {
      ...currentFlow,
      nodes: currentFlow.nodes.filter((node) => node.id !== nodeId),
      updatedAt: new Date(),
    };

    set({ currentFlow: updatedFlow });
  },

  updateFlowSettings: (settings) => {
    const { currentFlow } = get();
    if (!currentFlow) return;

    const updatedFlow = {
      ...currentFlow,
      ...settings,
      updatedAt: new Date(),
    };

    set({ currentFlow: updatedFlow });
  },

  saveFlow: async (flow) => {
    set({ isLoading: true, error: null });

    try {
      const { flowsApi } = await import('../services/api');

      let savedFlow;
      const { flows } = get();
      const existingIndex = flows.findIndex((f) => f.id === flow.id);

      // Clean payload - remove system fields for API
      // Don't send status to preserve existing status (especially for published flows)
      const cleanFlow = {
        title: flow.title,
        description: flow.description,
        startDate: flow.startDate,
        endDate: flow.endDate,
        maximumRegister: flow.maximumRegister
          ? Number(flow?.maximumRegister)
          : undefined,
        nodes: flow.nodes,
        settings: flow.settings,
        theme: flow.theme,
        // status is intentionally omitted to preserve existing status
      };

      savedFlow = await flowsApi.update(flow.id, cleanFlow);
      if (existingIndex >= 0) {
        // Update existing flow
        flows[existingIndex] = convertFlowDates(savedFlow);
      }

      set({
        flows: [...flows],
        currentFlow: convertFlowDates(savedFlow),
        isLoading: false,
      });

      // Show success toast if available
      if (typeof window !== 'undefined' && (window as any).showToast) {
        (window as any).showToast('success', 'Flow saved successfully!');
      }
    } catch (error) {
      console.error('Save flow error:', error);
      set({ error: 'Failed to save flow', isLoading: false });
    }
  },

  publishFlow: async (flowId) => {
    set({ isLoading: true, error: null });

    try {
      const { flowsApi } = await import('../services/api');
      const publishedFlow = await flowsApi.publish(flowId);

      const { currentFlow, flows } = get();
      const flowIndex = flows.findIndex((f) => f.id === flowId);

      if (flowIndex >= 0) {
        flows[flowIndex] = convertFlowDates(publishedFlow);
      }

      if (currentFlow && currentFlow.id === flowId) {
        set({ currentFlow: convertFlowDates(publishedFlow) });
      }

      set({ flows: [...flows], isLoading: false });
    } catch (error) {
      console.error('Publish flow error:', error);
      set({ error: 'Failed to publish flow', isLoading: false });
    }
  },

  unpublishFlow: async (flowId) => {
    set({ isLoading: true, error: null });

    try {
      const { flowsApi } = await import('../services/api');
      const unpublishedFlow = await flowsApi.unpublish(flowId);

      const { currentFlow, flows } = get();
      const flowIndex = flows.findIndex((f) => f.id === flowId);

      if (flowIndex >= 0) {
        flows[flowIndex] = convertFlowDates(unpublishedFlow);
      }

      if (currentFlow && currentFlow.id === flowId) {
        set({ currentFlow: convertFlowDates(unpublishedFlow) });
      }

      set({ flows: [...flows], isLoading: false });
    } catch (error) {
      console.error('Unpublish flow error:', error);
      set({ error: 'Failed to unpublish flow', isLoading: false });
    }
  },

  deleteFlow: async (flowId) => {
    set({ isLoading: true, error: null });

    try {
      const { flowsApi } = await import('../services/api');
      await flowsApi.delete(flowId);

      const { currentFlow, flows } = get();
      const updatedFlows = flows.filter((flow) => flow.id !== flowId);

      set({ flows: updatedFlows });

      // Clear current flow if it's the one being deleted
      if (currentFlow && currentFlow.id === flowId) {
        set({ currentFlow: null });
      }

      set({ isLoading: false });
    } catch (error) {
      console.error('Delete flow error:', error);
      set({ error: 'Failed to delete flow', isLoading: false });
    }
  },

  loadFlows: async () => {
    set({ isLoading: true, error: null });

    try {
      const { flowsApi } = await import('../services/api');
      const { flows } = await flowsApi.getAll();

      set({ flows: flows.map(convertFlowDates), isLoading: false });
    } catch (error) {
      console.error('Load flows error:', error);

      // Fallback to demo data if API is not available
      const mockFlows: any[] = [
        {
          id: 'flow_1',
          title: 'Customer Feedback Form',
          description: 'Collect customer feedback and ratings',
          nodes: [],
          settings: {
            allowMultipleSubmissions: false,
            showProgressBar: true,
            requireAuth: false,
            collectAnalytics: appConfig.features.analytics,
          },
          theme: { ...defaultTheme },
          createdAt: new Date(),
          updatedAt: new Date(),
          status: 'draft',
        },
      ];

      set({ flows: mockFlows, isLoading: false });
    }
  },

  createNewFlow: async (title?: string) => {
    set({ isLoading: true, error: null });

    try {
      const newFlow: Omit<SharedFlow, 'id' | 'createdAt' | 'updatedAt'> = {
        title: title || 'Untitled Flow',
        nodes: [],
        settings: {
          allowMultipleSubmissions: false,
          showProgressBar: true,
          requireAuth: false,
          collectAnalytics: appConfig.features.analytics,
        },
        theme: { ...defaultTheme },
        status: 'draft',
      };

      // Try to create via API first
      try {
        const { flowsApi } = await import('../services/api');

        // Clean payload for API
        const cleanFlow = {
          title: newFlow.title,
          description: newFlow.description,
          nodes: newFlow.nodes,
          settings: newFlow.settings,
          theme: newFlow.theme,
          status: newFlow.status,
        };

        const savedFlow = await flowsApi.create(cleanFlow);

        const { flows } = get();
        set({
          currentFlow: convertFlowDates(savedFlow),
          flows: [...flows, convertFlowDates(savedFlow)],
          isLoading: false,
        });

        return convertFlowDates(savedFlow);
      } catch (apiError) {
        console.warn('API not available, creating local flow:', apiError);

        // Fallback to local creation
        const localFlow: SharedFlow = {
          id: `flow_${Date.now()}`,
          ...newFlow,
          createdAt: new Date(),
          updatedAt: new Date(),
        };

        set({ currentFlow: localFlow, isLoading: false });
        return localFlow;
      }
    } catch (error) {
      console.error('Failed to create new flow:', error);
      set({ error: 'Failed to create new flow', isLoading: false });
      throw error;
    }
  },

  saveFlowTranslation: async (flow: SharedFlow, lang: string) => {
    try {
      set({ isLoading: true, error: null });
      const { flowsApi } = await import('../services/api');

      const cleanFlow = {
        title: flow.title,
        description: flow.description,
        nodes: flow.nodes,
      };

      const savedFlow = await flowsApi.saveTranslation(
        flow.id,
        lang,
        cleanFlow
      );

      set({
        currentFlow: convertFlowDates(savedFlow),
        isLoading: false,
      });

      // Show success toast if available
      if (typeof window !== 'undefined' && (window as any).showToast) {
        (window as any).showToast('success', 'Flow saved successfully!');
      }
    } catch (error) {
      console.error('Save flow error:', error);
      set({ error: 'Failed to save flow', isLoading: false });
    }
  },
}));
